import { Flex } from '@quark/layout'
import { Text } from '@quark/typography'
import React, { useEffect, useRef, useState } from 'react'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { AnimatedMobileTitlesProps } from '@interfaces/home'
import { Domain } from '@enums/Domain'
import { cardContentTextConfig } from '@configs/cardContent.config'
import styles from './CardContent.module.scss'
import { parseHtmlContent } from './parseContent'

// Animated title component for featured mobile content
const AnimatedMobileTitles = ({ titles, titleFontStyle, textAlign, domain }: AnimatedMobileTitlesProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const cycleCountRef = useRef(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (titles.length <= 1) return

    // Clear any existing intervals/timeouts
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current)
      animationTimeoutRef.current = null
    }

    // Reset cycle count
    cycleCountRef.current = 0
    setCurrentIndex(0)
    setIsAnimating(false)

    // Start the animation cycle after initial 2 second delay
    const initialTimeout = setTimeout(() => {
      const animate = () => {
        // Check if we've completed 6 cycles
        if (cycleCountRef.current >= 6) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
          setCurrentIndex(0)
          setIsAnimating(false)
          return
        }

        // Start animation
        setIsAnimating(true)

        // After animation completes, update index
        animationTimeoutRef.current = setTimeout(() => {
          setCurrentIndex(prev => {
            const nextIndex = (prev + 1) % titles.length
            // If we've cycled back to the first title, increment cycle count
            if (nextIndex === 0) {
              cycleCountRef.current += 1
            }
            return nextIndex
          })
          setIsAnimating(false)
        }, 500) // Animation duration
      }

      // Start interval for animation
      intervalRef.current = setInterval(animate, 2000) // 2 second interval
    }, 2000) // Initial 2 second delay

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current)
        animationTimeoutRef.current = null
      }
      clearTimeout(initialTimeout)
    }
  }, [titles.length])

  if (titles.length === 0) return null
  if (titles.length === 1) {
    return (
      <Text
        as="h4"
        display={cardContentTextConfig?.[domain]?.displayMobile}
        fontStyle={titleFontStyle}
        color="base/primary"
        whiteSpace="pre-line"
        textAlign={textAlign}>
        {titles?.[0]}
      </Text>
    )
  }

  const currentTitle = titles[currentIndex]
  const nextTitle = titles[(currentIndex + 1) % titles.length]

  return (
    <div className={styles.animatedTitleContainer}>
      <div key={`current-${currentIndex}`} className={`${styles.animatedTitle} ${isAnimating ? styles.slideUp : ''}`}>
        <Text
          as="h4"
          display={cardContentTextConfig?.[domain]?.displayMobile}
          fontStyle={titleFontStyle}
          color="base/primary"
          whiteSpace="pre-line"
          textAlign={textAlign}>
          {currentTitle}
        </Text>
      </div>
      {isAnimating && (
        <div key={`next-${(currentIndex + 1) % titles.length}`} className={`${styles.animatedTitle} ${styles.slideIn}`}>
          <Text
            as="h4"
            display={cardContentTextConfig?.[domain]?.displayMobile}
            fontStyle={titleFontStyle}
            color="base/primary"
            whiteSpace="pre-line"
            textAlign={textAlign}>
            {nextTitle}
          </Text>
        </div>
      )}
    </div>
  )
}

const CardContent = ({
  desktopContent,
  mobileContent,
  domain = Domain.ACKO,
  isFeatured = false,
}: {
  desktopContent?: string
  mobileContent?: string
  domain?: Domain
  isFeatured?: boolean
}) => {
  const parsedDesktopContent = parseHtmlContent(desktopContent || '', domain === Domain.ACKODRIVE)
  const parsedMobileContent = parseHtmlContent(mobileContent || '', domain === Domain.ACKODRIVE)
  return (
    <Flex
      flexDirection="column"
      gap={cardContentTextConfig?.[domain].specificGap as any}
      alignItems={cardContentTextConfig?.[domain].alignItems as any}>
      <PropertyControlledComponent controllerProperty={!!parsedDesktopContent?.titles} fallback={null}>
        <Text
          as="h4"
          display={cardContentTextConfig?.[domain]?.displayDesktop as any}
          fontStyle={cardContentTextConfig?.[domain]?.titleFontStyle as any}
          color="base/primary"
          whiteSpace="pre-line"
          textAlign={cardContentTextConfig?.[domain]?.textAlign as any}>
          {parsedDesktopContent?.titles}
        </Text>
      </PropertyControlledComponent>
      <PropertyControlledComponent controllerProperty={!!parsedDesktopContent?.subtitles} fallback={null}>
        <Text
          display={cardContentTextConfig?.[domain]?.displayDesktop as any}
          fontStyle={cardContentTextConfig?.[domain]?.subtitleFontStyle as any}
          color="base/secondary"
          whiteSpace="pre-line"
          textAlign={cardContentTextConfig?.[domain]?.textAlign as any}>
          {parsedDesktopContent?.subtitles}
        </Text>
      </PropertyControlledComponent>
      <PropertyControlledComponent
        controllerProperty={isFeatured}
        fallback={
          parsedMobileContent?.titles && (
            <Text
              as="h4"
              display={cardContentTextConfig?.[domain]?.displayMobile as any}
              fontStyle={cardContentTextConfig?.[domain]?.titleFontStyle as any}
              color="base/primary"
              whiteSpace="pre-line"
              textAlign={cardContentTextConfig?.[domain]?.textAlign as any}>
              {parsedMobileContent?.titles}
            </Text>
          )
        }>
        {parsedMobileContent?.titlesArray && parsedMobileContent.titlesArray.length > 0 && (
          <AnimatedMobileTitles
            titles={parsedMobileContent?.titlesArray}
            titleFontStyle={cardContentTextConfig?.[domain]?.titleFontStyle as any}
            textAlign={cardContentTextConfig?.[domain]?.textAlign as any}
            domain={domain}
          />
        )}
      </PropertyControlledComponent>
      <PropertyControlledComponent controllerProperty={!!parsedMobileContent?.subtitles} fallback={null}>
        <Text
          display={cardContentTextConfig?.[domain]?.displayMobile as any}
          fontStyle={cardContentTextConfig?.[domain]?.subtitleFontStyle as any}
          color="base/secondary"
          whiteSpace="pre-line"
          textAlign={cardContentTextConfig?.[domain]?.textAlign as any}>
          {parsedMobileContent?.subtitles}
        </Text>
      </PropertyControlledComponent>
    </Flex>
  )
}

export default CardContent
